import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  BarChart3, Users, GraduationCap, Building2, Network, School, DoorOpen,
  Video, CalendarClock, Sprout, FileDown, Loader2, ClipboardCheck,
} from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, accent = "#1F2547", testid }) => (
  <div className="jasq-card p-4 flex items-center gap-3" data-testid={testid}>
    <div className="w-11 h-11 rounded-2xl grid place-items-center shrink-0" style={{ backgroundColor: `${accent}14`, color: accent }}>
      <Icon size={20} strokeWidth={2.2} />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-extrabold text-[#1F2547] leading-tight">{value}</p>
      <p className="text-xs text-[#6E7591] truncate">{label}</p>
    </div>
  </div>
);

const Bar = ({ label, value, max, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs">
      <span className="font-bold text-[#1F2547] truncate">{label}</span>
      <span className="text-[#6E7591]">{value}</span>
    </div>
    <div className="h-2.5 rounded-full bg-[#F2F4FA] overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${max ? (value / max) * 100 : 0}%`, backgroundColor: color }} />
    </div>
  </div>
);

export default function AdminReportes() {
  const { user: me } = useAuth();
  // v5.6.4 · el admin general puede filtrar el reporte (y el PDF) por franquicia.
  const [franchises, setFranchises] = useState([]);
  const [franchiseId, setFranchiseId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingMaster, setDownloadingMaster] = useState(false);

  useEffect(() => {
    if (me?.role !== "admin") return;
    api.get("/franchises").then((r) => setFranchises(r.data || [])).catch(() => {});
  }, [me?.role]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/reports/executive", { params: franchiseId ? { franchise_id: franchiseId } : {} });
        setData(data);
      } catch {
        toast.error("No se pudo cargar el reporte.");
      } finally {
        setLoading(false);
      }
    })();
  }, [franchiseId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/executive/pdf", { responseType: "blob", params: franchiseId ? { franchise_id: franchiseId } : {} });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const _fr = franchises.find((f) => f.franchise_id === franchiseId);
      a.download = `reporte_ejecutivo${_fr ? "_" + _fr.name.replace(/[^\w]+/g, "_") : ""}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Reporte PDF descargado.");
    } catch {
      toast.error("No se pudo generar el PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadMaster = async () => {
    setDownloadingMaster(true);
    try {
      const res = await api.get("/reports/master-doc/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `documento_maestro_qa_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Documento maestro QA descargado.");
    } catch {
      toast.error("No se pudo generar el documento maestro.");
    } finally {
      setDownloadingMaster(false);
    }
  };

  if (loading && !data) {
    return <div className="jasq-card p-10 text-center text-sm text-[#6E7591]" data-testid="reportes-loading">Cargando reporte…</div>;
  }
  if (!data) return null;

  const k = data.kpis;
  const a = data.assignments;
  const maxDist = Math.max(1, ...data.distribution.map((d) => d.count));
  const maxFr = Math.max(1, ...data.by_franchise.map((d) => d.count));
  const statusColors = ["#A43790", "#2C49A0", "#F5B82E", "#D63384", "#16A34A", "#6E7591"];

  return (
    <div className="space-y-6" data-testid="admin-reportes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Reportes Ejecutivos</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
            <BarChart3 className="text-[#A43790]" /> Panel de indicadores
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">
            Alcance: <b>{data.scope === "franquicia" ? (data.scope_name ? `Franquicia · ${data.scope_name}` : "Mi franquicia") : "Global (todas las franquicias)"}</b>
            {loading && <span className="ml-2 text-[11px] text-[#A43790]">actualizando…</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
        {me?.role === "admin" && (
          <label className="inline-flex items-center gap-2 text-xs font-bold text-[#4C5478]">
            Franquicia
            <select
              data-testid="report-franchise-filter"
              value={franchiseId}
              onChange={(e) => setFranchiseId(e.target.value)}
              className="px-3 py-2.5 rounded-2xl border-2 border-[#E1E5F0] bg-white text-sm font-bold text-[#1F2547]"
            >
              <option value="">Todas (global)</option>
              {franchises.map((f) => (
                <option key={f.franchise_id} value={f.franchise_id}>{f.is_headquarters ? "★ " : ""}{f.name}</option>
              ))}
            </select>
          </label>
        )}
        <button
          onClick={downloadPdf}
          disabled={downloading}
          data-testid="download-report-pdf"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-[#1F2547] text-white font-bold text-sm hover:bg-[#2C49A0] transition-colors disabled:opacity-60"
        >
          {downloading ? <Loader2 size={18} className="animate-spin" /> : <FileDown size={18} />}
          Descargar PDF
        </button>
        <button
          onClick={downloadMaster}
          disabled={downloadingMaster}
          data-testid="download-master-doc"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-[#A43790] text-[#A43790] font-bold text-sm hover:bg-[#FDEBF4] transition-colors disabled:opacity-60"
        >
          {downloadingMaster ? <Loader2 size={18} className="animate-spin" /> : <FileDown size={18} />}
          Documento maestro QA
        </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reportes-kpis">
        <KpiCard icon={Users} label="Estudiantes" value={k.students} accent="#D63384" testid="kpi-students" />
        <KpiCard icon={GraduationCap} label="Docentes" value={k.teachers} accent="#2C49A0" testid="kpi-teachers" />
        <KpiCard icon={ClipboardCheck} label="Diagnosticados" value={k.diagnosed} accent="#16A34A" testid="kpi-diagnosed" />
        <KpiCard icon={Sprout} label="Índice crecimiento (prom.)" value={k.avg_growth_index} accent="#A43790" testid="kpi-growth" />
        <KpiCard icon={Building2} label="Franquicias" value={k.franchises} accent="#F5B82E" testid="kpi-franchises" />
        <KpiCard icon={Network} label="Instituciones" value={k.institutions} accent="#2C49A0" testid="kpi-institutions" />
        <KpiCard icon={School} label="Colegios" value={k.schools} accent="#A43790" testid="kpi-schools" />
        <KpiCard icon={DoorOpen} label="Salones" value={k.classrooms} accent="#1F2547" testid="kpi-classrooms" />
        <KpiCard icon={Video} label="Clases realizadas" value={k.classes_done} accent="#16A34A" testid="kpi-classes-done" />
        <KpiCard icon={CalendarClock} label="Clases próximas" value={k.classes_upcoming} accent="#F5B82E" testid="kpi-classes-upcoming" />
        <KpiCard icon={GraduationCap} label="Docentes certificados" value={k.certified_teachers} accent="#2C49A0" testid="kpi-certified" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Evaluaciones */}
        <div className="jasq-card p-5 lg:col-span-1" data-testid="reportes-assignments">
          <h2 className="font-display font-extrabold text-[#1F2547] mb-3">Diagnósticos (CEP · CAD)</h2>
          <div className="text-center mb-3">
            <p className="text-4xl font-extrabold text-[#A43790]">{a.completion_rate}%</p>
            <p className="text-xs text-[#6E7591]">de completitud · {a.total} asignadas</p>
          </div>
          <div className="space-y-2">
            <Bar label="Completadas" value={a.completed} max={a.total} color="#16A34A" />
            <Bar label="En progreso" value={a.in_progress} max={a.total} color="#F5B82E" />
            <Bar label="Pendientes" value={a.pending} max={a.total} color="#D63384" />
          </div>
        </div>

        {/* Distribución */}
        <div className="jasq-card p-5 lg:col-span-2" data-testid="reportes-distribution">
          <h2 className="font-display font-extrabold text-[#1F2547] mb-3">Distribución por estado cualitativo</h2>
          {data.distribution.length === 0 ? (
            <p className="text-sm text-[#6E7591]">Aún no hay datos de estado.</p>
          ) : (
            <div className="space-y-3">
              {data.distribution.map((d, i) => (
                <Bar key={d.status} label={d.status} value={d.count} max={maxDist} color={statusColors[i % statusColors.length]} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Por franquicia (solo admin global) */}
      {data.by_franchise.length > 0 && (
        <div className="jasq-card p-5" data-testid="reportes-by-franchise">
          <h2 className="font-display font-extrabold text-[#1F2547] mb-3">Estudiantes por franquicia</h2>
          <div className="space-y-3">
            {data.by_franchise.map((d) => (
              <Bar key={d.franchise} label={d.franchise} value={d.count} max={maxFr} color="#2C49A0" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
